import { useEffect, useRef, useState, useCallback } from "react";
import confetti from "canvas-confetti";
import { Row, RowState } from "./Row";
import dictionary from "./dictionary.json";
import { Clue, clue, CluedLetter, describeClue, fibclue, fibcount } from "./clue";
import { Keyboard } from "./Keyboard";
import targetList from "./targets.json";
import {
  gameName,
  pick,
  speak,
  dayNum,
  todayDayNum,
  cheat,
  maxGuesses,
  makeRandom,
  practice,
  practiceSeed,
  urlParam,
  isDev
} from "./util";
import { hardCodedPuzzles  } from "./hardcoded";
import { Day } from "./Stats"
import cheatyface from "./cheatyface.json";
import { PopUp } from "./PopUp";
import { getTakeoverDismissKey, maybeFetchTakeover } from "./takeover";
import TakeoverNotice from "./TakeoverNotice";
import type { Takeover } from "./takeover";
import { getUserId } from "./userId";
import { notePlayed } from "./activePing";
import { saveGameState, RemoteGameState, findWatchableGames, WatchableGames } from "./gameStateApi";
import { WatchView } from "./WatchView";

export enum GameState {
  Playing,
  Won,
  Lost,
}

export const gameDayStoragePrefix = "fibble.results-";
export const guessesDayStoragePrefix = "fibble.guesses-";
export const flagsDayStoragePrefix = "fibble.flags-";
export const lockDayStoragePrefix = "fibble.locks-";

export interface Fib
{
  position: number;
  offset: number;
}

function useLocalStorage<T>(
  key: string,
  initial: T
): [T, (value: T | ((t: T) => T)) => void] {
  const [current, setCurrent] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initial;
    } catch (e) {
      return initial;
    }
  });
  const setSetting = (value: T | ((t: T) => T)) => {
    try {
      const v = value instanceof Function ? value(current) : value;
      setCurrent(v);
      window.localStorage.setItem(key, JSON.stringify(v));
    } catch (e) {}
  };
  return [current, setSetting];
}

function useLocalStorageWriteImmediately<T>(
  key: string,
  initial: T
): [T, (value: T | ((t: T) => T)) => void] {
  const [current, setCurrent] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (!item) {
        window.localStorage.setItem(key, JSON.stringify(initial));
      }
      return item ? JSON.parse(item) : initial;
    } catch (e) {
      window.localStorage.setItem(key, JSON.stringify(initial));
      return initial;
    }
  });
  const setSetting = (value: T | ((t: T) => T)) => {
    try {
      const v = value instanceof Function ? value(current) : value;
      setCurrent(v);
      window.localStorage.setItem(key, JSON.stringify(v));
    } catch (e) {}
  };
  return [current, setSetting];
}

interface GameProps {
  maxGuesses: number;
  hidden: boolean;
  colorBlind: boolean;
  showFibCounts: boolean;
  hardModeSetting: boolean;
  keyboardLayout: string;
  liveReplays: boolean;
  setLiveReplays: (value: boolean | ((t: boolean) => boolean)) => void;
}

const eligible = targetList.slice(0, targetList.indexOf("murky") + 1).filter((word) => word.length === 5); // Words no rarer than this one

function isValidClue(word: string) {
  if (/\*/.test(word)) {
    return false;
  }
  return true;
}

function countMatching(cluedLetters: CluedLetter[]) : Map<Clue, number> {
  let counts = new Map<Clue,number>();
  for (const letter of cluedLetters) {
    let clue = letter.clue;
    if (clue) {
      let count = counts.get(clue) ?? 0;
      counts.set(clue, count+1);
    }
  }
  return counts;
}

function isGoodInitialGuess(target: string, candidate: string) {
  if (/\*/.test(candidate)) {
    return false;
  }
  if (candidate.length !== target.length) {
    return false;
  }
  let hints = clue(candidate, target);
  let green = countMatching(hints).get(Clue.Correct) ?? 0;
  let yellow = countMatching(hints).get(Clue.Elsewhere) ?? 0;
  return green + yellow < 5;
}

function randomTarget(random: ()=>number): string {
  let candidate: string;
  do {
    candidate = pick(eligible, random);
  } while (!isValidClue(candidate));
  return candidate;
}

function initialGuess(target: string, random: ()=>number): [string] {
  let candidate: string;
  do {
    candidate = pick(eligible, random);
  } while(!isGoodInitialGuess(target, candidate));
  return [candidate];
}

function initialFlags(): number[] {
  let flags = new Array<number>(maxGuesses);
  for (let i = 0; i < maxGuesses; ++i) { 
    flags[i] = -1;
  }
  return flags;
}

function initialLocks(): boolean[][] {
  let locks = new Array<Array<boolean>>(maxGuesses);
  for (let i = 0; i < maxGuesses; ++i) { 
    locks[i] = Array(5).fill(false);
  }
  return locks;
}

function gameOverText(state: GameState, target: string) : string {
  const verbed = state === GameState.Won ? "won" : "lost";
  return `You ${verbed}! It was ${target.toUpperCase()}.`; 
}

let uniqueGame = practice ? 200000 : 2000;
export function makePuzzle(seed: number) : Puzzle {
  let hardCoded = hardCodedPuzzles[seed];
  if (hardCoded && !practice) {
      return hardCoded;
  }  
  let random = makeRandom(seed+uniqueGame);
  let target = randomTarget(random);
  let fibs = new Array<Fib>(maxGuesses);
  const positions = [0,1,2,3,4];
  for (let i = 0; i < maxGuesses; ++i) {
	  fibs[i]= { position: pick(positions, random), offset: pick([1,2], random) };
  }
 
  let puzzle: Puzzle = {
    target: target,
    initialGuesses: initialGuess(target, random),
    fibs: fibs
  };
  return puzzle;
}

export function emojiBlock(day: Day, colorBlind: boolean) : string {
  const emoji = colorBlind
    ? ["⬛", "🟦", "🟧"]
    : ["⬛", "🟨", "🟩"];
  return day.guesses.map((guess, i) =>
    (day.gameState === GameState.Won && i === (day.guesses.length-1) ? clue(guess,day.puzzle.target) : fibclue(guess, day.puzzle.target, day.puzzle.fibs[i]) )  
          .map((c) => emoji[c.clue ?? 0])
          .join("")
      )
      .join("\n");
}

export interface Puzzle {
  target: string,
  initialGuesses: string[],
  fibs: Array<Fib>
}

function Game(props: GameProps) {

  if (urlParam("export") && isDev) {
    let values : Record<number, Puzzle> = {};    
    for(let i = 1; i <= parseInt(urlParam("export") ?? "1"); ++i) {
      values[i] = makePuzzle(i);
    }
    window.console.log( JSON.stringify(values, null, "\t") );
  }

  const resetPractice = () => {
    if (practice) {
      window.localStorage.removeItem("practice");
      window.localStorage.removeItem("practiceState");
      window.localStorage.removeItem("practiceGuesses");
      window.localStorage.removeItem("practiceFlags");
      window.localStorage.removeItem("practiceLocks");
    }
  }

  const resetDay = () => {
    if (isDev) {
      window.localStorage.removeItem(gameDayStoragePrefix+dayNum);
      window.localStorage.removeItem(guessesDayStoragePrefix+dayNum);
      window.localStorage.removeItem(flagsDayStoragePrefix+seed);
      window.localStorage.removeItem(lockDayStoragePrefix+seed);
    }
  }
    
  let seed: number = dayNum;
  if (practice) {
    seed = new Date().getMilliseconds() + (1000 * (1 + new Date().getTime() % 10));
    if (!(new URLSearchParams(window.location.search).has("new"))) {
      try {
        let storedSeed = window.localStorage.getItem("practice");
        if (storedSeed) {
          seed = parseInt(storedSeed);
          if (practiceSeed && seed !== practiceSeed) {
            resetPractice();
            seed = practiceSeed;            
          }
        } else {
          seed = practiceSeed ?? seed;
          window.localStorage.setItem("practice",""+seed);
        }
        } catch(e) {
      }
    }
  }
  
  const [puzzle, setPuzzle] = useState(() => {
   return makePuzzle(seed);
  });

  let stateStorageKey = practice ? "practiceState" : (gameDayStoragePrefix+seed);
  let guessesStorageKey = practice ? "practiceGuesses" : (guessesDayStoragePrefix+seed);
  let flagStorageKey = practice ? "practiceFlags" : (flagsDayStoragePrefix+seed);
  let lockStorageKey = practice ? "practiceLocks" : (lockDayStoragePrefix+seed);
  
  const [gameState, setGameState] = useLocalStorage<GameState>(stateStorageKey, GameState.Playing);
  const [guesses, setGuesses] = useLocalStorage<string[]>(guessesStorageKey, puzzle.initialGuesses);

  const [currentGuess, setCurrentGuess] = useState<string>("");
  const [hint, setHint] = useState<string>(getHintFromState());
  const [flags, setFlags] = useLocalStorage<number[]>(flagStorageKey, initialFlags());
  const [locks, setLocks] = useLocalStorage<boolean[][]>(lockStorageKey, initialLocks());
  const [debug, setDebug] = useState<string>();
  const [keyboardJuice, setKeyboardJuice] = useState(false);
  const juicedAtGuessCount = useRef<number>(-1);

  // --- Watch feature state ---
  const puzzleId = practice ? `practice-${seed}` : `${seed}`;
  const userId = getUserId();

  const [watchGames, setWatchGames] = useState<WatchableGames>({ liveGames: [], replayGames: [] });
  const [showWatchIntro, setShowWatchIntro] = useState(false);
  const [watching, setWatching] = useState(false);
  const [watchingKind, setWatchingKind] = useState<"live" | "replay" | null>(null);
  const [watchingUserId, setWatchingUserId] = useState<string | undefined>(undefined);

  const liveCount = watchGames.liveGames.length;
  const replayCount = watchGames.replayGames.length;

  const watchingIndexInList = (() => {
    if (!watching || !watchingUserId || !watchingKind) return 0;
    const list = watchingKind === "live" ? watchGames.liveGames : watchGames.replayGames;
    const idx = list.findIndex((g) => g.user_id === watchingUserId);
    return idx >= 0 ? idx + 1 : 1;
  })();
  const watchingTotal = watchingKind === "live" ? liveCount : replayCount;

  function syncToRemote(allGuesses: string[], typing: string, status: "in_progress" | "won" | "lost", currentFlags: number[]) {
    if (!props.liveReplays || cheat) return;
    const serializedGuesses = allGuesses.map((word) => ({
      word,
      clues: [] as [],
    }));
    const payload: RemoteGameState = {
      metadata: { status, numGuesses: allGuesses.length, puzzleId },
      state: { guesses: serializedGuesses, currentGuess: typing, flags: currentFlags },
    };
    saveGameState(userId, puzzleId, payload);
  }

  // Sync existing state on mount
  useEffect(() => {
    if (gameState === GameState.Won) {
      syncToRemote(guesses, "", "won", flags);
    } else if (gameState === GameState.Lost) {
      syncToRemote(guesses, "", "lost", flags);
    } else if (gameState === GameState.Playing) {
      syncToRemote(guesses, currentGuess, "in_progress", flags);
    }
  }, []);

  // Poll for watchable games after game over
  useEffect(() => {
    if (gameState !== GameState.Won && gameState !== GameState.Lost) return;
    let active = true;
    async function poll() {
      while (active) {
        const result = await findWatchableGames(puzzleId, userId);
        if (!active) return;
        setWatchGames(result);
        await new Promise((r) => setTimeout(r, 10_000));
      }
    }
    poll();
    return () => { active = false; };
  }, [gameState, puzzleId, userId]);

  function pickGame(kind: "live" | "replay", afterUserId?: string): RemoteGameState | null {
    const list = kind === "live" ? watchGames.liveGames : watchGames.replayGames;
    if (list.length === 0) return null;
    if (!afterUserId) return list[0];
    const currentIdx = list.findIndex((g) => g.user_id === afterUserId);
    const nextIdx = (currentIdx + 1) % list.length;
    return list[nextIdx];
  }

  function startWatchingKind(kind: "live" | "replay") {
    const game = pickGame(kind);
    if (!game) return;
    setWatchingKind(kind);
    setWatchingUserId(game.user_id);
    setWatching(true);
  }

  function switchToKind(kind: "live" | "replay") {
    const afterId = kind === watchingKind ? watchingUserId : undefined;
    const game = pickGame(kind, afterId);
    if (!game) return;
    setWatching(false);
    setTimeout(() => {
      setWatchingKind(kind);
      setWatchingUserId(game.user_id);
      setWatching(true);
    }, 0);
  }

  function handleWatchClick(kind: "live" | "replay") {
    const introSeen = window.localStorage.getItem("fibble-watch-intro-seen");
    if (!props.liveReplays || !introSeen) {
      setShowWatchIntro(true);
    } else {
      startWatchingKind(kind);
    }
  }
  // --- End watch feature state ---

  const tableRef = useRef<HTMLTableElement>(null);
  async function share(copiedHint: string, text?: string) {
    const url = window.location.origin + window.location.pathname + (practice ? ("?unlimited=" + seed.toString()) : "");     
    const body = (text ? text + "\n" : "") + url;
    if (
      /android|iphone|ipad|ipod|webos/i.test(navigator.userAgent) &&
      !/firefox/i.test(navigator.userAgent)
    ) {
      try {
        await navigator.share({ text: body });
        return;
      } catch (e) {
        console.warn("navigator.share failed:", e);
      }
    }
    try {
      await navigator.clipboard.writeText(body);
      setHint(copiedHint);
      return;
    } catch (e) {
      console.warn("navigator.clipboard.writeText failed:", e);
    }
    setHint(url);
  }

  function getHintFromState() {    
    if  (gameState === GameState.Won || gameState === GameState.Lost) {
      return gameOverText(gameState, puzzle.target);
    }
    if (guesses.length > 0 && 5 == countMatching(fibclue(guesses[guesses.length-1], puzzle.target, puzzle.fibs[guesses.length-1])).get(Clue.Correct))
       return "Not quite. Still lyin'";
    return guesses.length < 3 && !practice ? `Beware the lies.` : ``;
  }

  const onClickFlag = (row: number, position: number) => {
    
    if (flags[row] !== -1 && locks[row][flags[row]]) {
      // If we had a flag and it was unlocked, lock it.
      onToggleLock(row, flags[row]);
    }

    let newFlags = [...flags];
    newFlags[row] = newFlags[row] === position ? -1 : position;
    setFlags(newFlags);

    if (position !== -1 && locks[row][position]) {
      // If the new position is already locked, unlock it
      onToggleLock(row, position);
    }

    // Sync flag changes to remote
    const status = gameState === GameState.Won ? "won" : gameState === GameState.Lost ? "lost" : "in_progress";
    syncToRemote(guesses, currentGuess, status, newFlags);

    setDebug("Flagged");
  };

  const onToggleLock = (row: number, position: number) => {
    let newLocks = [...locks];
    newLocks[row][position] = !newLocks[row][position];
    setLocks(newLocks);  
    setDebug("Locked");
  };

  const onKey = (key: string) => {
    if (gameState !== GameState.Playing) {
      return;
    }

    if (guesses.length === props.maxGuesses) {
      return;
    }
    if (/^[a-z]$/i.test(key)) {
      setCurrentGuess((guess) => {
        const next = (guess + key.toLowerCase()).slice(0, 5);
        syncToRemote(guesses, next, "in_progress", flags);
        return next;
      });
      tableRef.current?.focus();
      setHint(getHintFromState());
    } else if (key === "Backspace") {
      setCurrentGuess((guess) => {
        const next = guess.slice(0, -1);
        syncToRemote(guesses, next, "in_progress", flags);
        return next;
      });
      setHint(getHintFromState());
    } else if (key === "Enter") {

      if (currentGuess.length !== 5) {
        setHint("More letters, please.");
        return;
      }
      if(guesses.includes(currentGuess)) {
        setHint("You've already guessed that!");
        return;
      }
      if (!dictionary.includes(currentGuess)) {
        setHint(`That's not in the word list!`);
        return;
      }
      if (props.hardModeSetting) {
        for(let i = 0; i < guesses.length; ++i) {
          const guess = guesses[i];
          let clue = fibclue(guess, puzzle.target, puzzle.fibs[i]);
          let fibs = fibcount(currentGuess, guess, clue);
          if (fibs !== 1) {
            setHint(guess.toUpperCase() + " would have " + fibs.toString() + " lies.");
            return;
          }
        }
      }

      const nextGuesses = guesses.concat([currentGuess]);
      setGuesses((guesses) => guesses.concat([currentGuess]));
      setCurrentGuess("");
      speak(describeClue(fibclue(currentGuess, puzzle.target, puzzle.fibs[guesses.length])));

      // Determine status after this guess
      if (nextGuesses.includes(puzzle.target)) {
        syncToRemote(nextGuesses, "", "won", flags);
      } else if (nextGuesses.length >= props.maxGuesses) {
        syncToRemote(nextGuesses, "", "lost", flags);
      } else {
        syncToRemote(nextGuesses, "", "in_progress", flags);
      }
      notePlayed("fibble", cheat || isDev);

      doWinOrLose();
    }
  };

  const applyJuice = (rowIndex: number, className: string, tableClassName?: string) => {
    const tbody = tableRef.current?.querySelector("tbody");
    if (!tbody) return;
    const row = tbody.children[rowIndex] as HTMLElement | undefined;
    if (!row) return;
    className.split(" ").forEach(c => row.classList.add(c));
    const letters = row.querySelectorAll(".Row-letter");
    const target = letters.length > 0 ? letters[letters.length - 1] : row;
    target.addEventListener("animationend", () => {
      className.split(" ").forEach(c => row.classList.remove(c));
    }, { once: true });
    if (tableClassName && tableRef.current) {
      tableRef.current.classList.add(tableClassName);
      tableRef.current.addEventListener("animationend", () => {
        tableRef.current?.classList.remove(tableClassName);
      }, { once: true });
    }
  };

  const burstFromRow = (rowIndex: number, particlesPerLetter: number) => {
    const tbody = tableRef.current?.querySelector("tbody");
    if (!tbody) return;
    const row = tbody.children[rowIndex] as HTMLElement | undefined;
    if (!row) return;
    const letters = row.querySelectorAll(".Row-letter");
    const count = letters.length;
    letters.forEach((el, i) => {
      const rect = el.getBoundingClientRect();
      const x = (rect.left + rect.width / 2) / window.innerWidth;
      const y = (rect.bottom + 8) / window.innerHeight;
      const angle = 90 - (i - (count - 1) / 2) * 8;
      confetti({ particleCount: particlesPerLetter, spread: 30, angle, origin: { x, y }, gravity: 1.2, startVelocity: 15 });
    });
  };

  const doWinOrLose = () => {
    if (puzzle.target === "") {
      return;
    }
    if (guesses.includes(puzzle.target)) {
      setGameState(GameState.Won);
      if (juicedAtGuessCount.current !== guesses.length) {
        juicedAtGuessCount.current = guesses.length;
        const winRow = guesses.length - 1;
        const userGuesses = guesses.length - puzzle.initialGuesses.length;
        const isEpic = userGuesses <= 2;
        if (isEpic) {
          applyJuice(winRow, "Row-juice-win-epic", "Game-rows-juice-win-epic");
          burstFromRow(winRow, 18);
          setTimeout(() => burstFromRow(winRow, 18), 400);
        } else {
          applyJuice(winRow, "Row-juice-win", "Game-rows-juice-win");
          burstFromRow(winRow, 15);
        }
        setKeyboardJuice(true);
      }
    } else if (guesses.length >= props.maxGuesses) {
      setGameState(GameState.Lost);
    }
    setHint(getHintFromState());
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        onKey(e.key);
      }
      if (e.key === "Backspace") {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [currentGuess, gameState]);

  useEffect(() => {
    doWinOrLose();
  }, [currentGuess, gameState, guesses, puzzle.target]);

  let reduceCorrect = (prev: CluedLetter, iter: CluedLetter, currentIndex: number, array: CluedLetter[]) => {
    let reduced: CluedLetter = prev;
    if ( iter.clue !== Clue.Correct ) {
      reduced.clue = Clue.Absent;
    }
    return reduced;
  };

  let letterTrue = new Map<string, boolean>();
  let letterInfo = new Map<string, Clue>();
  const tableRows = Array(props.maxGuesses)
    .fill(undefined)
    .map((_, i) => {
      const guess = [...guesses, currentGuess][i] ?? "";
      const finalWinningGuess = gameState === GameState.Won && i === guesses.length-1;
      const cluedLetters = finalWinningGuess ? clue(guess,puzzle.target) : fibclue(guess, puzzle.target, puzzle.fibs[i]);
      const lockedIn = i < guesses.length;
      const lies = lockedIn && props.showFibCounts ? fibcount(currentGuess, guess, cluedLetters) : 0;
      const annotation = (lockedIn && lies > 0) ? lies.toString() : "\u00a0";
      if (lockedIn) {
        for (let j = 0; j < cluedLetters.length; ++j) {
          const { clue, letter } = cluedLetters[j];
          if (clue === undefined) break;
          if (flags[i] == j) continue;
          if (locks[i][j]) { letterTrue.set(letter, true); }
          const old = letterInfo.get(letter);
          if (old === undefined || clue > old) {
            letterInfo.set(letter, clue);
          }
        }
      }
      return (
        <Row
          key={i}
          rowNum={i}
          fibPos={ (gameState === GameState.Playing || i >= guesses.length-1 ) ? -1 : puzzle.fibs[i].position }
          flagPos={
            (gameState === GameState.Won && i === guesses.length-1 ) ? -1 : flags[i]}
          rowState={
            lockedIn
              ? RowState.LockedIn
              : (i === guesses.length)
              ? RowState.Editing
              : RowState.Pending
          }
          locks={locks}
          cluedLetters={cluedLetters}
          clickHandler={(row: number, position: number) => { if( gameState === GameState.Playing ) { onClickFlag(row, position); } } }
          contextHandler={(row: number, position: number)=> { if( gameState === GameState.Playing ) { onToggleLock(row, position); } } }
          annotation={annotation} 
        />
      );
    });

  const cheatText = cheat ? ` ${puzzle.target}` : "";
  const canPrev = dayNum > 1 || isDev;
  const canNext = dayNum < todayDayNum || isDev;
  const todayLink = "?";
  const practiceLink = "?unlimited";
  const prevLink = "?x=" + (dayNum-1).toString() + (isDev ? "&xyzzyx="+cheatyface["password"] : "") + (cheat ? "&cheat=1" : "");
  const nextLink = "?x=" + (dayNum+1).toString() + (isDev ? "&xyzzyx="+cheatyface["password"] : "") + (cheat ? "&cheat=1" : "");
 
  let correctFlags = 0;
  let totalFlags = 0;
  for (let i = 0; i < maxGuesses; ++i) {
    if (puzzle.fibs[i].position === flags[i]) {
      correctFlags++;
    }
    if (flags[i] !== -1) {
      totalFlags++;
    }
  }

  const takeoverSiteId = gameName.toLowerCase();
  const shouldCheckTakeover =
    !watching &&
    !practice &&
    dayNum === todayDayNum &&
    gameState !== GameState.Playing;

  const [takeover, setTakeover] = useState<Takeover | null>(null);
  useEffect(() => {
    if (!shouldCheckTakeover) return;
    let canceled = false;
    (async () => {
      const t = await maybeFetchTakeover(takeoverSiteId);
      if (canceled) return;
      setTakeover(t);
    })();
    return () => {
      canceled = true;
    };
  }, [shouldCheckTakeover, takeoverSiteId]);

  // Any deliberate action means they are done with it, on this device, for good.
  const dismissTakeover = (takeoverId: string) => {
    try {
      window.localStorage.setItem(getTakeoverDismissKey(takeoverSiteId, takeoverId), "1");
    } catch (e) {
      // ignore storage failures
    }
  };

  const flagShare = (totalFlags > 0) ? (" " + correctFlags.toString() + "/" + totalFlags.toString() + "🏴") : "";
  const hintClass = 
    guesses.length <= 1 ? "shake-short" : "";
  
  let hardModeDetected = true;
  for (let i = 0; i < guesses.length; ++i) {
    for(let j=0; j < i; ++j) {
      let prevoius_clue = fibclue(guesses[j], puzzle.target, puzzle.fibs[j])
      if (1 !== fibcount(guesses[i], guesses[j], prevoius_clue)) {
        hardModeDetected = false;
      }
    }
  }

  const hardModeShare = hardModeDetected ? " 🔥🔥🔥" : "";

  return (
    <div className="Game" style={{ display: props.hidden ? "none" : "block" }}>
      {!watching && showWatchIntro && (
        <PopUp
          title="Watch Mode"
          element={
            <div>
              <p>When enabled, you can watch other players' solutions live or as a replay
                 after solving a puzzle yourself, and likewise your solution goes into the
                 pool for others to watch live or as a replay.</p>
              <p>You can change this anytime in Settings.</p>
              <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "18px" }}>
                <button className="watch-button" onClick={() => {
                  localStorage.setItem("fibble-watch-intro-seen", "1");
                  props.setLiveReplays(true);
                  setShowWatchIntro(false);
                  if (liveCount > 0) startWatchingKind("live");
                  else if (replayCount > 0) startWatchingKind("replay");
                }}>
                  &#9654; Enable and watch
                </button>
                <button className="modal-dismiss-button" onClick={() => setShowWatchIntro(false)}>
                  Dismiss
                </button>
              </div>
            </div>
          }
          onClick={() => setShowWatchIntro(false)}
        />
      )}
      <div className="Game-options">
        {!practice && canPrev && <span><a href={prevLink}>Previous</a> |</span>}
        {!practice && <span>Day {dayNum}{`${cheatText}`}</span>}
        {!practice && canNext && <span>| <a href={nextLink}>Next</a></span>}
        {practice && <span>{`${cheatText}`}</span>}
        {practice && <span><a href={practiceLink} onClick={ ()=>{resetPractice();} }>+ New Puzzle</a></span>}
        {isDev && <span>| <a href={window.location.href} onClick={ ()=>{resetDay();} }>Reset</a></span>}
      </div>

      {watching && (
        <WatchView
          puzzleId={puzzleId}
          colorBlind={props.colorBlind}
          keyboardLayout={props.keyboardLayout}
          targetUserId={watchingUserId}
          watchLabel={
            watchingKind === "live"
              ? `Watching Live (${watchingIndexInList} of ${watchingTotal})`
              : `Replay (${watchingIndexInList} of ${watchingTotal})`
          }
          onStatusChange={(status) => {
            if (watchingKind === "live" && status !== "in_progress") {
              setWatchingKind("replay");
            }
          }}
        >
          <div className="button-stack">
            {watchingKind === "live" && liveCount > 1 && (
              <button className="watch-button" onClick={() => switchToKind("live")}>
                Next Live ({liveCount})
              </button>
            )}
            {watchingKind === "live" && replayCount > 0 && (
              <button className="watch-button watch-button--replay" onClick={() => switchToKind("replay")}>
                Watch Replay ({replayCount})
              </button>
            )}
            {watchingKind === "replay" && liveCount > 0 && (
              <button className="watch-button" onClick={() => switchToKind("live")}>
                Watch Live ({liveCount})
              </button>
            )}
            {watchingKind === "replay" && replayCount > 1 && (
              <button className="watch-button watch-button--replay" onClick={() => switchToKind("replay")}>
                Next Replay ({replayCount})
              </button>
            )}
            <button className="copy-result-button" onClick={() => setWatching(false)}>
              Your Solution
            </button>
          </div>
        </WatchView>
      )}

      {!watching && (
        <>
          <table
            className="Game-rows"
            tabIndex={0}
            aria-label="table of guesses"
            ref={tableRef}
          >
            <tbody>{tableRows}</tbody>
          </table>
          <p
            role="alert"
            style={{
              userSelect: /https?:/.test(hint) ? "text" : "none",
              whiteSpace: "pre-wrap",
            }}
            className={hintClass}
          >
            {hardModeDetected && (props.showFibCounts || gameState == GameState.Won) && `🔥`}
            {hint || `\u00a0`}
            {hardModeDetected && (props.showFibCounts || gameState == GameState.Won) && `🔥`}
            {gameState !== GameState.Playing && (
              <span>
                <div className="button-stack">
                  <button
                    className="copy-result-button"
                    onClick={() => {
                      const emoji = props.colorBlind
                        ? ["⬛", "🟦", "🟧"]
                        : ["⬛", "🟨", "🟩"];
                      const score = gameState === GameState.Lost ? "X" : (guesses.length-1);
                      share(
                        "Result copied to clipboard!",
                        `${gameName} ${practice ? ("Unlimited " + seed.toString()) : ("#"+dayNum.toString())} ${score}/${props.maxGuesses-1}${hardModeShare}${flagShare}\n` +
                          emojiBlock({guesses:guesses, puzzle:puzzle, gameState:gameState, flags:flags}, props.colorBlind)
                      );
                    }}
                  >
                    Share
                  </button>
                  {liveCount > 0 && (
                    <button className="watch-button" onClick={() => handleWatchClick("live")}>
                      Watch Live ({liveCount})
                    </button>
                  )}
                  {replayCount > 0 && (
                    <button className="watch-button watch-button--replay" onClick={() => handleWatchClick("replay")}>
                      Watch Replay ({replayCount})
                    </button>
                  )}
                </div>
              </span>
            )}
          </p>
          {shouldCheckTakeover && takeover && (
            <TakeoverNotice
              takeover={takeover}
              onDismiss={() => dismissTakeover(takeover.id)}
            />
          )}
          <Keyboard
            layout={props.keyboardLayout}
            letterInfo={letterInfo}
            letterTrue={letterTrue}
            onKey={onKey}
            juiceGreen={keyboardJuice}
            onJuiceDone={() => setKeyboardJuice(false)}
          />
          <div className="debug">{debug}</div>
        </>
      )}
    </div>
  );
}

export default Game;
